import React from 'react';
import {
    AppBar, Box, Button, Chip, Container, Dialog, Divider,
    Grid, IconButton, List, ListItem, ListItemButton, ListItemText,
    Paper, Typography, CircularProgress, Tooltip, Toolbar
} from "@mui/material";
import {
    RemoveRedEye, ContentCopy, LocationOn,
    Phone, Work, Group, Person, Close as CloseIcon,
    Info
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import ExclusiveDatagrid from '../../../../components/ui/ExclusiveDatagrid';
import { GridActionsCellItem } from '@mui/x-data-grid';

const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        borderRadius: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            margin: theme.spacing(1),
            borderRadius: theme.spacing(1),
        }
    }
}));

const StyledListItem = styled(ListItemButton)(({ theme }) => ({
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(1),
    transition: 'all 0.2s ease',
    '&:hover': {
        backgroundColor: theme.palette.primary.main + '08',
        transform: 'translateX(4px)',
    },
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(1.5),
    }
}));

const DetailSection = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(3),
    '& .section-title': {
        display: 'flex',
        alignItems: 'center',
        marginBottom: theme.spacing(2),
        '& .MuiSvgIcon-root': {
            marginRight: theme.spacing(1),
            color: theme.palette.primary.main,
        }
    }
}));

const JobModal = ({
    open,
    onClose,
    jobTarget,
    jobTargetJobWorkers,
    jobTargetJobAuthorities,
    onTargetAim,
    onCopyToClipboard
}) => {
    return (
        <StyledDialog
            fullScreen
            open={open}
            onClose={onClose}
        >
            <AppBar position="relative" elevation={0} sx={{ backgroundColor: 'background.paper' }}>
                <Toolbar>
                    <IconButton edge="start" color="primary" onClick={onClose} aria-label="close">
                        <CloseIcon />
                    </IconButton>
                    <Typography sx={{ ml: 2, flex: 1, color: 'text.primary' }} variant="h6">
                        {jobTarget?.isyeriUnvani}
                    </Typography>
                </Toolbar>
            </AppBar>

            <Container maxWidth="xl" sx={{ py: 4 }}>
                <Grid container spacing={3}>
                    {/* Company Information */}
                    <Grid item xs={12} md={6}>
                        <DetailSection>
                            <Box className="section-title">
                                <Work />
                                <Typography variant="h6">Şirket Bilgileri</Typography>
                            </Box>
                            <Paper elevation={0} sx={{ p: 2, backgroundColor: 'background.default' }}>
                                <List>
                                    {[
                                        { label: 'Ünvan', value: jobTarget?.isyeriUnvani },
                                        { label: 'İşyeri Kodu', value: jobTarget?.isyeriId },
                                        { label: 'SGK Sicil No', value: jobTarget?.isyeriSgkSicilNo },
                                        { label: 'NACE Kodu', value: jobTarget?.isyeriNaceKodu },
                                        { label: 'Tehlike Sınıfı', value: jobTarget?.isyeriTehlikeSinifi },
                                        { label: 'İş Tanımı', value: jobTarget?.isyeriSektoru }
                                    ].map((item, index) => (
                                        <StyledListItem 
                                            key={index}
                                            onClick={() => onCopyToClipboard(item.value, item.label)}
                                        >
                                            <ListItemText 
                                                primary={item.label}
                                                secondary={item.value}
                                                primaryTypographyProps={{
                                                    variant: 'subtitle2',
                                                    color: 'textSecondary'
                                                }}
                                                secondaryTypographyProps={{
                                                    variant: 'body1',
                                                    color: 'textPrimary'
                                                }}
                                            />
                                            <ContentCopy color="action" fontSize="small" />
                                        </StyledListItem>
                                    ))}
                                </List>
                            </Paper>
                        </DetailSection>
                    </Grid>

                    {/* Authorities */}
                    <Grid item xs={12}>
                        <DetailSection>
                            <Box className="section-title">
                                <Group />
                                <Typography variant="h6">Yetkililer</Typography>
                            </Box>
                            <Paper elevation={0} sx={{ p: 2, backgroundColor: 'background.default' }}>
                                <ExclusiveDatagrid
                                    columns={[
                                        { field: 'yetkiliTckn', headerName: 'TC', width: 150 },
                                        { field: 'yetkiliAdSoyad', headerName: 'Ad Soyad', width: 200 },
                                        { field: 'yetkiTuru', headerName: 'Yetki Türü', width: 200 },
                                        { field: 'yetkililikDurumu', headerName: 'Durum', width: 140 }
                                    ]}
                                    data={jobTargetJobAuthorities ?? []}
                                    startWithActions={true}
                                    actions={[
                                        (params) => (
                                            <Tooltip title="Detayları Görüntüle">
                                                <GridActionsCellItem
                                                    icon={<RemoveRedEye />}
                                                    onClick={() => onTargetAim(params.row.yetkiliTckn)}
                                                    label="Details"
                                                />
                                            </Tooltip>
                                        )
                                    ]}
                                />
                            </Paper>
                        </DetailSection>
                    </Grid>

                    {/* Employees */}
                    <Grid item xs={12}>
                        <DetailSection>
                            <Box className="section-title">
                                <Group />
                                <Typography variant="h6">Çalışanlar</Typography>
                            </Box>
                            <Paper elevation={0} sx={{ p: 2, backgroundColor: 'background.default' }}>
                                <ExclusiveDatagrid
                                    columns={[
                                        { field: 'calisanId', headerName: 'Çalışan ID', width: 110 },
                                        { field: 'belgeTur', headerName: 'Belge Türü', width: 130 },
                                        { field: 'calisanKimlikNo', headerName: 'TC', width: 150 },
                                        { field: 'calisanAdSoyad', headerName: 'Ad Soyad', width: 200 },
                                        { field: 'isciId', headerName: 'Sicil No', width: 90 },
                                        { field: 'iseGirisTarihi', headerName: 'Giriş Tarihi', width: 260 },
                                        { field: 'calismaDurumu', headerName: 'Durum', width: 140 }
                                    ]}
                                    data={jobTargetJobWorkers ?? []}
                                    startWithActions={true}
                                    actions={[
                                        (params) => (
                                            <Tooltip title="Detayları Görüntüle">
                                                <GridActionsCellItem
                                                    icon={<RemoveRedEye />}
                                                    onClick={() => onTargetAim(params.row.calisanKimlikNo)}
                                                    label="Details"
                                                />
                                            </Tooltip>
                                        )
                                    ]}
                                />
                            </Paper>
                        </DetailSection>
                    </Grid>
                </Grid>
            </Container>
        </StyledDialog>
    );
};

export default JobModal; 